import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { DiamondShapeIcon, DIAMOND_SHAPES } from "../components/DiamondShapeIcon";

const TABS = [
  { slug: "", label: "All" },
  { slug: "rings", label: "Rings" },
  { slug: "necklaces", label: "Necklaces" },
  { slug: "earrings", label: "Earrings" },
  { slug: "bracelets", label: "Bracelets" },
  { slug: "ring-bands", label: "Ring Bands" },
];

const TITLES = {
  rings: "Rings",
  necklaces: "Necklaces",
  earrings: "Earrings",
  bracelets: "Bracelets",
  "ring-bands": "Ring Bands",
};

export default function Shop() {
  const { category } = useParams();
  const [products, setProducts] = useState(null);
  const [params, setParams] = useSearchParams();
  const [shape, setShape] = useState("");
  const [availableShapes, setAvailableShapes] = useState(null);

  useEffect(() => {
    if (params.get("checkout") === "cancelled") {
      toast("Checkout cancelled. Your selection is still saved.");
      params.delete("checkout");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  useEffect(() => {
    setShape("");
  }, [category]);

  useEffect(() => {
    if (category === "rings" || category === "ring-bands") {
      api.get("/shape-availability")
        .then(({ data }) => setAvailableShapes(data.visible.map((v) => v.shape)))
        .catch(() => setAvailableShapes(DIAMOND_SHAPES));
    } else {
      setAvailableShapes(null);
    }
  }, [category]);

  useEffect(() => {
    setProducts(null);
    const q = {};
    if (category) q.category = category;
    if (shape) q.shape = shape;
    api
      .get("/products", { params: q })
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]));
  }, [category, shape]);

  const showShapeFilter = (category === "rings" || category === "ring-bands") && availableShapes && availableShapes.length > 0;

  return (
    <div data-testid="shop-page" className="pt-8 max-w-[1400px] mx-auto px-6 sm:px-12 min-h-screen">
      <p className="label-track text-[#A67C3D] mb-2">The Collection</p>
      <h1 className="font-heading font-bold text-3xl sm:text-5xl text-[#0A0A0A] mb-6">
        {category ? TITLES[category] || "Pieces" : "The Full Atelier"}
      </h1>

      <div className="flex gap-8 border-b border-[#E5E5E5] mb-8 overflow-x-auto">
        {TABS.map((t) => {
          const active = (category || "") === t.slug;
          return (
            <Link
              key={t.label}
              to={t.slug ? `/shop/${t.slug}` : "/shop"}
              data-testid={`shop-tab-${t.label.toLowerCase()}`}
              className={`pb-4 label-track whitespace-nowrap transition-colors border-b ${
                active ? "text-[#A67C3D] border-[#A67C3D]" : "text-[#737373] border-transparent hover:text-[#0A0A0A]"
              }`}
            >
              {t.label}
            </Link>
          );
        })}
      </div>

      {showShapeFilter && (
        <div className="mb-8" data-testid="shape-filter">
          <p className="label-track text-[#737373] mb-3">
            Diamond Shape {shape && <span className="text-[#0A0A0A] normal-case tracking-normal">— {shape}</span>}
          </p>
          <div className="flex gap-3 overflow-x-auto pb-2">
            <button
              onClick={() => setShape("")}
              data-testid="shape-filter-all"
              className={`shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 border transition-all ${
                shape === "" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
              }`}
            >
              <span className="w-9 h-9 flex items-center justify-center text-[10px] font-mono">ALL</span>
              <span className="label-track text-[9px]">All Shapes</span>
            </button>
            {DIAMOND_SHAPES.filter((s) => availableShapes.includes(s)).map((s) => (
              <button
                key={s}
                onClick={() => setShape(s)}
                data-testid={`shape-filter-${s.toLowerCase()}`}
                className={`shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 border transition-all ${
                  shape === s ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
                }`}
              >
                <DiamondShapeIcon shape={s} className="w-9 h-9" />
                <span className="label-track text-[9px]">{s}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {products === null && <p className="text-[#737373] py-20 text-center">Loading the collection…</p>}

      {products && products.length === 0 && (
        <div className="py-24 text-center">
          <p className="font-heading text-2xl text-[#0A0A0A] mb-3">
            {shape ? `No ${shape} pieces yet.` : "The vitrine awaits its first piece."}
          </p>
          <p className="text-[#737373] text-sm">
            {shape ? "Try another shape or clear the filter." : "Pieces appear here once imported from the studio."}
          </p>
        </div>
      )}

      {products && products.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 pb-12">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
